import { createFocusTrap } from '../lib/focus-trap';
import { hideBackground, showBackground } from '../utils/util-functions';

// Which mounted header may write --header-offset. Module-level because the Theme Editor can
// have a stale header and a live one alive at once, and only the owner may write or reset it.
let offsetOwner = null;

export default (node) => {
  const body = document.querySelector('body');
  const header = node.querySelector('.header');
  const main = document.querySelector('main');
  const headerBar = node.querySelector('[data-header-bar]');
  const headerNav = node.querySelector('[data-header-nav]');
  const template = node.dataset.template;
  const headerLinks = node.querySelectorAll('[data-header-link]');
  const mobileMenuLink = node.querySelector('[data-mobile-menu-link]');
  const mobileMenu = node.querySelector('[data-mobile-menu]');
  const megaMenuWrapper = node.querySelector('[data-megamenus]');
  const megaMenus = node.querySelectorAll('[data-megamenu]');
  const megaMenuOverlay = node.querySelector('[data-megamenu-overlay]');

  let lastScrollY = window.scrollY;
  let activeMenuTrigger = null;
  let destroyFocusTrap = null;
  let navHeight = 0;
  let barHeight = 0;
  let headerOffset = null;

  const slideTiming = () => {
    const slide = header
      .getAnimations()
      .find((animation) => animation.transitionProperty === 'transform');
    if (!slide) return undefined;

    const { duration, easing } = slide.effect.getTiming();
    return { duration, easing };
  };

  // Publish half of the --header-offset pair; retractHeaderOffset is the other. Heights come
  // from the ResizeObserver only: reading them here, right after the scroll handler toggles a
  // class, would force a layout on every scroll event.
  const setHeaderOffset = () => {
    // Before the first measurement this would publish 0px and hand the next change a
    // `previous` of 0 to animate from.
    if (offsetOwner !== node || !navHeight) return;

    const barVisible =
      headerBar && !header.classList.contains('-translate-y-8');
    const offset = navHeight + (barVisible ? barHeight : 0);
    if (offset === headerOffset) return;
    const previous = headerOffset;
    headerOffset = offset;
    document.documentElement.style.setProperty(
      '--header-offset',
      `${offset}px`
    );
    if (previous === null) return;

    document.dispatchEvent(
      new CustomEvent('header:offset', {
        detail: { previous, timing: slideTiming() },
      })
    );
  };

  const retractHeaderOffset = () => {
    if (offsetOwner !== node) return;

    document.documentElement.style.removeProperty('--header-offset');
    offsetOwner = null;
  };

  const observeHeaderHeights = () => {
    if (!headerNav) {
      console.warn(
        '[header] Missing [data-header-nav]; --header-offset will not be published.'
      );
      return;
    }

    offsetOwner = node;
    const observer = new ResizeObserver(() => {
      // After a Theme Editor reload this instance is left observing a detached nav that
      // measures 0x0.
      if (!node.isConnected) {
        observer.disconnect();
        retractHeaderOffset();
        return;
      }

      navHeight = headerNav.offsetHeight;
      barHeight = headerBar ? headerBar.offsetHeight : 0;
      setHeaderOffset();
    });
    observer.observe(headerNav);
    if (headerBar) observer.observe(headerBar);
  };

  const handleHeaderBarPosition = () => {
    if (!headerBar) return;

    const currentScrollY = window.scrollY;
    const isScrollingDown = currentScrollY > lastScrollY;

    // Hide header bar by translating the entire header up
    if (isScrollingDown && currentScrollY > 32) {
      header.classList.add('-translate-y-8');
    }
    // Show header bar when scrolling up or at the top
    else if (!isScrollingDown || currentScrollY <= 32) {
      header.classList.remove('-translate-y-8');
    }

    lastScrollY = currentScrollY;
    setHeaderOffset();
  };

  const handleHeaderTransparency = () => {
    const firstSection = main.querySelector('.shopify-section');
    const headerInner = node.querySelector('[data-header-inner]');

    // Both are required to compute transparency and both are expected to always
    // exist: firstSection because this listener is only wired up when the first
    // section is present and transparent (see init), and headerInner because
    // [data-header-inner] is rendered unconditionally in header.liquid. A miss
    // therefore signals a markup defect — warn loudly rather than silently misrender.
    if (!firstSection || !headerInner) {
      console.warn(
        '[header] Missing first section or [data-header-inner]; skipping transparency update.'
      );
      return;
    }

    // Solid once the first section's bottom has scrolled up past the visible header.
    if (
      firstSection.getBoundingClientRect().bottom <=
      headerInner.getBoundingClientRect().bottom
    ) {
      header.classList.remove('header--transparent');
    } else if (node.dataset.template === 'index') {
      header.classList.add('header--transparent');
    }
  };

  const handleMobileMenuOpen = () => {
    body.classList.add('!overflow-hidden');
    header.classList.add('menu-open');
    mobileMenuLink.classList.add('open');
    mobileMenuLink.setAttribute('aria-expanded', 'true');
    mobileMenu.classList.remove('animate-fade-up');
    mobileMenu.classList.remove('hidden');
    mobileMenu.classList.add('animate-fade-down');
    hideBackground('mobile-menu');
    destroyFocusTrap = createFocusTrap(mobileMenu);
  };

  const handleMobileMenuClose = () => {
    body.classList.remove('!overflow-hidden');
    header.classList.remove('menu-open');
    mobileMenuLink.classList.remove('open');
    mobileMenuLink.setAttribute('aria-expanded', 'false');
    mobileMenu.classList.add('animate-fade-up');
    mobileMenu.classList.add('hidden');
    mobileMenu.classList.remove('animate-fade-down');
    showBackground('mobile-menu');
    if (destroyFocusTrap) {
      destroyFocusTrap();
      destroyFocusTrap = null;
    }
    mobileMenuLink.focus();
  };

  const handleMenuOpen = (linkTitle) => {
    megaMenus.forEach((megamenu) => {
      megamenu.classList.add('closed');
      megamenu.setAttribute('aria-hidden', 'true');
    });
    headerLinks.forEach((link) => {
      link.setAttribute('aria-expanded', 'false');
    });
    let megaMenu = [...megaMenus].filter((megamenu) => {
      return megamenu.dataset.megamenuMenuPoint === linkTitle;
    })[0];
    if (megaMenu) {
      megaMenu.classList.remove('closed');
      megaMenu.setAttribute('aria-hidden', 'false');
      const activeLink = [...headerLinks].find(
        (link) => link.innerText === linkTitle
      );
      if (activeLink) activeLink.setAttribute('aria-expanded', 'true');
      body.classList.add('!overflow-hidden');
      header.classList.add('menu-open');
      megaMenuWrapper.classList.add('menu-open');
      hideBackground('mega-menu');
      if (destroyFocusTrap) destroyFocusTrap();
      destroyFocusTrap = createFocusTrap(megaMenuWrapper);
    } else {
      handleMenuClose();
    }
  };

  const handleMenuClose = () => {
    megaMenus.forEach((megamenu) => {
      megamenu.classList.add('closed');
      megamenu.setAttribute('aria-hidden', 'true');
    });
    headerLinks.forEach((link) => {
      link.setAttribute('aria-expanded', 'false');
    });
    body.classList.remove('!overflow-hidden');
    megaMenuWrapper.classList.remove('menu-open');
    showBackground('mega-menu');
    if (destroyFocusTrap) {
      destroyFocusTrap();
      destroyFocusTrap = null;
    }
    setTimeout(() => {
      header.classList.remove('menu-open');
    }, 400);
    if (activeMenuTrigger) {
      activeMenuTrigger.focus();
      activeMenuTrigger = null;
    }
  };

  const init = () => {
    const isFirstSectionTransparent = main
      .querySelector('.shopify-section')
      ?.classList.contains('transparent-section');

    headerLinks.forEach((headerLink) => {
      headerLink.addEventListener('click', (evt) => {
        evt.preventDefault();
        activeMenuTrigger = headerLink;
        const linkTitle = headerLink.innerText;
        handleMenuOpen(linkTitle);
      });
      headerLink.addEventListener('mouseenter', () => {
        const linkTitle = headerLink.innerText;
        if (header.classList.contains('menu-open')) {
          handleMenuOpen(linkTitle);
        }
      });
    });

    megaMenuOverlay.addEventListener('click', () => {
      handleMenuClose();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        if (header.classList.contains('menu-open')) {
          handleMenuClose();
        }
        if (mobileMenuLink.classList.contains('open')) {
          handleMobileMenuClose();
        }
      }
    });

    mobileMenuLink.addEventListener('click', (e) => {
      e.preventDefault();
      if (mobileMenuLink.classList.contains('open')) {
        handleMobileMenuClose();
      } else {
        handleMobileMenuOpen();
      }
    });

    document.addEventListener('scroll', (e) => {
      handleHeaderBarPosition();
    });

    observeHeaderHeights();

    const alwaysSolid = node.dataset.alwaysSolid === 'true';

    if (alwaysSolid || !isFirstSectionTransparent || template !== 'index') {
      header.classList.remove('header--transparent');
      return;
    }

    document.addEventListener('scroll', (e) => {
      handleHeaderTransparency();
    });

    header.addEventListener('mouseenter', () => {
      header.classList.remove('header--transparent');
    });

    header.addEventListener('mouseleave', () => {
      handleHeaderTransparency();
    });
  };

  init();
};
